import {
  Anvil,
  Coins,
  Cross,
  HeartPulse,
  KeyRound,
  LockKeyhole,
  Mountain,
  RefreshCw,
  Shield,
  ShieldOff,
  Sparkles,
  Sword,
  Swords,
  TreePine,
  WandSparkles,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { type SkillImpactKind, playSkillImpact } from "../skillImpact";
import { type DamageElement, type GrowthSkill } from "../types";
import { playControlFeedback } from "../uiFeedback";
import { formatDisplayNumber } from "./format";

export function SkillIcon({ skillId, size = 20 }: { skillId: string; size?: number }) {
  switch (skillId) {
    case "rockfall":
    case "fireball":
      return <Sparkles size={size} />;
    case "rebuild":
      return <RefreshCw size={size} />;
    case "prospect":
    case "timber_transmute":
    case "wood_blessing":
      return <TreePine size={size} />;
    case "sword_strike":
    case "vein_blast":
    case "heavy_slash":
      return <Sword size={size} />;
    case "blade_storm":
    case "whirlwind":
    case "active_shield_slam":
    case "war_cry":
    case "armor_break":
    case "counter_charge":
      return <Swords size={size} />;
    case "stoneskin":
    case "timber_wall":
    case "mana_barrier":
    case "sanctuary":
      return <Shield size={size} />;
    case "cleanse":
    case "purification":
      return <ShieldOff size={size} />;
    case "smelt":
    case "ore_transmute":
      return <Anvil size={size} />;
    case "key_burst":
      return <KeyRound size={size} />;
    case "drain":
    case "life_drain":
    case "greater_heal":
      return <HeartPulse size={size} />;
    case "mend":
      return <Cross size={size} />;
    case "time_stop":
    case "revenge_stance":
    case "free_cast":
      return <LockKeyhole size={size} />;
    case "frost_nova":
    case "meteor":
      return <Sparkles size={size} />;
    case "harvest_order":
      return <Coins size={size} />;
    case "mana_burst":
    case "staff_transmute":
    case "resource_fusion":
      return <WandSparkles size={size} />;
    case "mass_mend":
    case "blessed_cleanse":
      return <HeartPulse size={size} />;
    default:
      return <WandSparkles size={size} />;
  }
}

export const SKILL_BRANCHES = ["resource", "warrior", "mage", "priest"] as const;

export const SKILL_BRANCH_LABELS: Record<(typeof SKILL_BRANCHES)[number], string> = {
  resource: "资源",
  warrior: "战士",
  mage: "法师",
  priest: "牧师",
};

export const MANA_TIER_LABELS: Record<string, string> = {
  light: "轻量",
  standard: "标准",
  heavy: "重型",
  ultimate: "终极",
};

export function manaTier(skill: GrowthSkill): string {
  if (skill.manaClass) return skill.manaClass;
  if (skill.manaCost <= 30) return "light";
  if (skill.manaCost <= 45) return "standard";
  if (skill.manaCost <= 60) return "heavy";
  return "ultimate";
}

export function SkillBranchIcon({
  branch,
  size = 18,
}: {
  branch: GrowthSkill["branch"];
  size?: number;
}) {
  if (branch === "resource") return <Mountain size={size} />;
  if (branch === "warrior") return <Swords size={size} />;
  if (branch === "mage") return <WandSparkles size={size} />;
  return <HeartPulse size={size} />;
}

export function skillCooldownState(
  cooldowns: Record<string, number> | undefined,
  durations: Record<string, number> | undefined,
  skillId: string,
  clock: number,
  fallbackDurationMs = 1,
) {
  const durationMs = Math.max(1, durations?.[skillId] ?? fallbackDurationMs);
  const remainingMs = Math.max(0, (cooldowns?.[skillId] ?? 0) - clock);
  return { durationMs, remainingMs };
}

export function formatCooldownMs(cooldownMs: number) {
  return `${formatDisplayNumber(cooldownMs / 1000, true)}s`;
}

export function skillResourceLimitText(skill: Pick<GrowthSkill, "id">, limit?: number) {
  return skill.id === "key_burst" && limit && limit > 0 ? ` · 本次最多消耗 ${limit} 把钥匙` : "";
}

export function SkillButton({
  icon,
  name,
  description,
  cost,
  cooldownRemainingMs = 0,
  cooldownDurationMs = 1,
  disabled,
  onClick,
  effectTarget = "enemy",
  effectKind = "magic",
  effectElement = "neutral",
}: {
  icon: React.ReactNode;
  name: string;
  description: string;
  cost: number;
  cooldownRemainingMs?: number;
  cooldownDurationMs?: number;
  disabled: boolean;
  onClick: () => void;
  effectTarget?: string;
  effectKind?: SkillImpactKind;
  effectElement?: DamageElement;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previousCooldown = useRef(cooldownRemainingMs);
  const stopImpact = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    const started = cooldownRemainingMs > previousCooldown.current + 250;
    previousCooldown.current = cooldownRemainingMs;
    if (!started) return;
    if (buttonRef.current) playControlFeedback(buttonRef.current, true);
    const scope = buttonRef.current?.closest("main");
    const selector =
      effectTarget === "board"
        ? ".board-host"
        : effectTarget === "self" || effectTarget === "ally" || effectTarget === "all_allies"
          ? ".player-card, .room-member.is-you, .race-vitals"
          : ".monster-sprite, .room-boss-sprite";
    const targets = [...(scope?.querySelectorAll<HTMLElement>(selector) ?? [])];
    if (targets.length === 0) {
      const fallback = scope?.querySelector<HTMLElement>("[data-effect-target=enemy]");
      if (fallback) targets.push(fallback);
    }
    if (targets.length > 0) {
      stopImpact.current?.();
      const stops = targets
        .slice(0, effectTarget === "all_enemies" ? 4 : 1)
        .map((target) => playSkillImpact(target, effectKind, effectElement));
      stopImpact.current = () => stops.forEach((stop) => stop());
    }
  }, [cooldownRemainingMs, effectTarget, effectKind, effectElement]);
  useEffect(() => () => stopImpact.current?.(), []);
  const progress = Math.min(100, (cooldownRemainingMs / Math.max(1, cooldownDurationMs)) * 100);
  const cooldownLabel =
    cooldownRemainingMs > 0
      ? `，冷却剩余 ${formatDisplayNumber(cooldownRemainingMs / 1000)} 秒`
      : "";
  return (
    <button
      ref={buttonRef}
      className={`skill-button${cooldownRemainingMs > 0 ? " is-cooling" : ""}`}
      disabled={disabled}
      onClick={onClick}
      aria-label={`${name}，${description}${cooldownLabel}`}
    >
      <i className="skill-cooldown-fill" style={{ width: `${progress}%` }} aria-hidden="true" />
      {icon}
      <span>{name}</span>
      <small>
        {cooldownRemainingMs > 0 ? `${formatDisplayNumber(cooldownRemainingMs / 1000)}s` : cost}
      </small>
      <span className="skill-tooltip" role="tooltip">
        {description}
        {cooldownLabel}
      </span>
    </button>
  );
}
